import { useCallback, useEffect, useMemo, useState } from 'react';

import SaveArtworkIcon from '@/assets/all-icons/save-artwork-icon';
import config from '@/config';
import useGoBack from '@/hooks/useGoBack';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import UserService from '@/services/user.service';
import SUARTE_WORDMARK_DATA_URL from '@/shared-components/icons/components/public/suarteWordmarkDataUrl';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import { getSsrArtGamesBootstrap } from '@/utils/ssrArtGamesBootstrap';

import ArtGameDeleteConfirmation from '../components/ArtGameDeleteConfirmation';
import ArtGameHeader from '../components/ArtGameHeader';
import RadioControl from '../radio/RadioControl';
import drawUsernameWordmark from '../utils/drawUsernameWordmark';

import KaleidoscopeControls from './components/KaleidoscopeControls';
import KaleidoscopeGallery from './components/KaleidoscopeGallery';
import KaleidoscopeStage from './components/KaleidoscopeStage';
import { applyCircleClip } from './rendering';
import { useKaleidoscopeEngine } from './useKaleidoscopeEngine';

import './index.css';

const shouldShowSuartePlusPopupFromResponse = (payload) =>
  Boolean(payload?.showSuartePlusPopup ?? payload?.data?.showSuartePlusPopup);

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('Missing image source'));
      return;
    }

    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = src;
  });

function readInitialKaleidoscopeSeoPayload() {
  const payload = getSsrArtGamesBootstrap('kaleidoscope');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function KaleidoscopeGame() {
  const t = useTranslations('common.newspaper.art_games.kaleidoscope');
  const galleryT = useTranslations('common.newspaper.art_games.kaleidoscope.gallery');
  const deleteT = useTranslations('common.newspaper.art_games.delete_dialog');
  const { state } = useStateHandler();
  const goBackHandler = useGoBack('/1/newspaper/art-games');
  const watermarkUsername = state.user_session?.user_username ?? null;
  const {
    availableForDrawing,
    bloomCanvasRef,
    bloomClassName,
    bloomKey,
    brushColor,
    brushSize,
    canUndo,
    canvasSize,
    changeFolds,
    containerRef,
    deletePattern,
    folds,
    hasStroke,
    isDeleting,
    isRevealed,
    isSaving,
    resetCanvas,
    revealPattern,
    revealStartAngle,
    saveToast,
    savedPatterns,
    savePattern,
    setBrushColor,
    setBrushSize,
    setWedgeFocusScale,
    displayStartAngle,
    strokeBudgetSlots,
    undoStroke,
    usedBudget,
    wedgeCanvasRef,
  } = useKaleidoscopeEngine({ onUpgradeRequired: () => setShowSuartePlusPopup(true) });
  const [previewPattern, setPreviewPattern] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [showSuartePlusPopup, setShowSuartePlusPopup] = useState(false);
  const [isDownloadRegistering, setIsDownloadRegistering] = useState(false);
  const [kaleidoscopeSeo, setKaleidoscopeSeo] = useState(() => readInitialKaleidoscopeSeoPayload());
  const isGalleryEmpty = savedPatterns.length === 0;

  useEffect(() => {
    let ignore = false;

    async function loadKaleidoscopeSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/art-games/kaleidoscope`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setKaleidoscopeSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load Kaleidoscope SEO payload', error);
        }
      }
    }

    loadKaleidoscopeSeo();

    return () => {
      ignore = true;
    };
  }, []);

  const handlePatternSelect = useCallback((pattern) => {
    setIsGalleryOpen(false);
    setPreviewPattern(pattern);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewPattern(null);
    setShowDeleteConfirmation(false);
  }, []);

  const handleDeletePreview = useCallback(async () => {
    if (!previewPattern) return;
    const wasDeleted = await deletePattern(previewPattern.id);
    if (wasDeleted) {
      setShowDeleteConfirmation(false);
      setPreviewPattern(null);
    }
  }, [deletePattern, previewPattern]);

  const handleOpenGallery = useCallback(() => {
    if (isGalleryEmpty) return;
    setIsGalleryOpen(true);
  }, [isGalleryEmpty]);

  const handleCloseGallery = useCallback(() => {
    setIsGalleryOpen(false);
  }, []);

  const downloadWithWatermark = useCallback(async () => {
    if (!previewPattern?.url) return;

    const filenameDate = previewPattern.timestamp
      ? new Date(previewPattern.timestamp).toISOString().split('T')[0]
      : 'kaleidoscope';

    const triggerDownload = (dataUrl) => {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `suarte-kaleidoscope-${filenameDate}.png`;
      link.rel = 'noopener';
      link.click();
    };

    try {
      const [baseImage, logoImage] = await Promise.all([
        loadImage(previewPattern.url),
        loadImage(SUARTE_WORDMARK_DATA_URL).catch(() => null),
      ]);

      const canvas = document.createElement('canvas');
      const width = baseImage.naturalWidth || baseImage.width;
      const height = baseImage.naturalHeight || baseImage.height;
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        triggerDownload(previewPattern.url);
        return;
      }

      ctx.save();
      applyCircleClip(ctx, width, height);
      ctx.drawImage(baseImage, 0, 0, width, height);

      if (logoImage) {
        // Keep the watermark inside the circular artwork, including older square saves.
        const watermarkInset = Math.min(width, height) * 0.15;
        ctx.translate(watermarkInset, watermarkInset);
        drawUsernameWordmark(
          ctx,
          logoImage,
          width - watermarkInset * 2,
          height - watermarkInset * 2,
          watermarkUsername,
        );
      }
      ctx.restore();

      triggerDownload(canvas.toDataURL('image/png'));
    } catch (error) {
      console.error('Failed to apply Kaleidoscope watermark download', error);
      triggerDownload(previewPattern.url);
    }
  }, [previewPattern, watermarkUsername]);

  const handleDownloadPreview = useCallback(async () => {
    if (!previewPattern?.url || isDownloadRegistering) return;

    setIsDownloadRegistering(true);
    try {
      const { response, data } = await UserService.registerKaleidoscopeDownload();
      if (shouldShowSuartePlusPopupFromResponse(data)) {
        setShowSuartePlusPopup(true);
        return;
      }

      if (!response?.ok) {
        console.error('Failed to register Kaleidoscope download', data);
        return;
      }

      await downloadWithWatermark();
    } catch (error) {
      console.error('Failed to register Kaleidoscope download', error);
    } finally {
      setIsDownloadRegistering(false);
    }
  }, [downloadWithWatermark, isDownloadRegistering, previewPattern?.url]);

  const previewTags = useMemo(() => {
    if (!previewPattern) return [];
    const tags = [];
    if (previewPattern.folds) {
      const foldLabelByValue = {
        6: t('stage.folds.six'),
        8: t('stage.folds.eight'),
        12: t('stage.folds.twelve'),
      };
      tags.push(foldLabelByValue[previewPattern.folds] || `${previewPattern.folds}`);
    }
    if (previewPattern.organicMode) {
      tags.push(t('gallery.tags.organic'));
    }
    if (previewPattern.smoothingEnabled === false) {
      tags.push(t('gallery.tags.raw_strokes'));
    }
    if (typeof previewPattern.strokeCount === 'number' && previewPattern.strokeCount >= 0) {
      tags.push(t('gallery.tags.strokes_count', { count: previewPattern.strokeCount }));
    }
    return tags;
  }, [previewPattern, t]);

  return (
    <>
      <StaticSeoHead
        title={kaleidoscopeSeo?.title || t('meta.title')}
        description={kaleidoscopeSeo?.metaDescription || t('meta.description')}
        canonicalUrl={kaleidoscopeSeo?.canonicalUrl}
        canonicalPath={kaleidoscopeSeo?.canonicalPath || '/newspaper/art-games/kaleidoscope'}
        robots={kaleidoscopeSeo?.robots}
        ogType={kaleidoscopeSeo?.ogType}
        image={kaleidoscopeSeo?.ogImage}
        pageType={kaleidoscopeSeo?.pageType || 'WebApplication'}
        structuredData={kaleidoscopeSeo?.structuredData}
      />
      <div className="kaleidoscope-page">
        <ArtGameHeader
          className="kaleidoscope__header"
          title={t('hero.title')}
          onBack={goBackHandler}
          rightSlot={(
            <>
              <RadioControl />
              <button
                type="button"
                className={`kaleidoscope__gallery-button${isGalleryEmpty ? '' : ' is-active'}`}
                onClick={handleOpenGallery}
                disabled={isGalleryEmpty}
                aria-label={galleryT('title')}
              >
                <span className="kaleidoscope__gallery-button-label">Saved</span>
                <SaveArtworkIcon className={`kaleidoscope__gallery-button-icon${isGalleryEmpty ? '' : ' fill'}`} />
              </button>
            </>
          )}
          onShare={() => {
            const shareUrl =
              typeof window !== 'undefined' && window.location?.origin
                ? `${window.location.origin}/newspaper/art-games/kaleidoscope`
                : 'https://suarte.art/newspaper/art-games/kaleidoscope';

            if (navigator?.share) {
              navigator.share({
                title: t('share.title'),
                text: t('share.text'),
                url: shareUrl,
              }).catch(() => {});
              return;
            }

            if (navigator?.clipboard?.writeText) {
              navigator.clipboard.writeText(shareUrl).catch(() => {});
            }
          }}
          shareAriaLabel={t('share.aria')}
        />

        <div className="kaleidoscope-layout">
          <KaleidoscopeStage
            availableForDrawing={availableForDrawing}
            bloomCanvasRef={bloomCanvasRef}
            bloomClassName={bloomClassName}
            bloomKey={bloomKey}
            brushColor={brushColor}
            canvasSize={canvasSize}
            containerRef={containerRef}
            folds={folds}
            isRevealed={isRevealed}
            onBrushColorChange={setBrushColor}
            onWedgeFocusScaleChange={setWedgeFocusScale}
            displayStartAngle={displayStartAngle}
            revealStartAngle={revealStartAngle}
            strokeBudgetSlots={strokeBudgetSlots}
            usedBudget={usedBudget}
            wedgeCanvasRef={wedgeCanvasRef}
          />

          <div className="kaleidoscope-sidebar">
            <KaleidoscopeControls
              brushSize={brushSize}
              canUndo={canUndo}
              folds={folds}
              hasStroke={hasStroke}
              isSaving={isSaving}
              isRevealed={isRevealed}
              onBrushSizeChange={setBrushSize}
              onFoldChange={changeFolds}
              onReset={resetCanvas}
              onReveal={revealPattern}
              onSave={savePattern}
              onUndo={undoStroke}
            />
          </div>
        </div>
      </div>
      <GenericPopup
        open={isGalleryOpen}
        className="kaleidoscope-gallery-popup"
        backgroundClassName="kaleidoscope-gallery-popup__background"
        opacity
      >
        <KaleidoscopeGallery
          patterns={savedPatterns}
          toastMessage={saveToast}
          onPatternSelect={handlePatternSelect}
          isPopup
          onClose={handleCloseGallery}
        />
      </GenericPopup>
      <GenericPopup
        open={Boolean(previewPattern)}
        onCloseAction={() => setShowDeleteConfirmation(true)}
        closeButtonText={deleteT('confirm')}
        onConfirm={handleDownloadPreview}
        confirmButtonText={t('preview.download')}
        className="kaleidoscope-preview-popup"
        opacity
      >
        {previewPattern && (
          <div className="kaleidoscope-preview">
            <XIcon onClick={handleClosePreview} />
            <div className="kaleidoscope-preview__image-wrap">
              <img
                src={previewPattern.url}
                alt={t('preview.image_alt')}
                className="kaleidoscope-preview__image"
              />
            </div>
            <div className="kaleidoscope-preview__meta">
              <p className="kaleidoscope-preview__timestamp">
                {new Date(previewPattern.timestamp).toLocaleString()}
              </p>
              {previewTags.length > 0 && (
                <ul className="kaleidoscope-preview__tags">
                  {previewTags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </GenericPopup>
      <ArtGameDeleteConfirmation
        open={showDeleteConfirmation}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeletePreview}
        isDeleting={isDeleting}
      />
      <SuartePlusPopup
        open={showSuartePlusPopup}
        onClose={() => setShowSuartePlusPopup(false)}
        highlightedBenefit="game_saves"
      />
    </>
  );
}
